import { FocusEventHandler, useCallback, useEffect, useRef } from "react";

export const MOBILE_FOOTER_VISIBILITY_EVENT =
  "novinhub:mobile-footer-visibility";

type MobileFooterVisibilityDetail = {
  hidden: boolean;
};

export type MobileFooterVisibilityEvent =
  CustomEvent<MobileFooterVisibilityDetail>;

function emitMobileFooterVisibility(hidden: boolean) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<MobileFooterVisibilityDetail>(
      MOBILE_FOOTER_VISIBILITY_EVENT,
      { detail: { hidden } },
    ),
  );
}

function getVisualKeyboardInset() {
  if (typeof window === "undefined" || !window.visualViewport) {
    return null;
  }

  return Math.max(
    0,
    window.innerHeight -
      window.visualViewport.height -
      window.visualViewport.offsetTop,
  );
}

export function useMobileFooterHiddenOnFocus<T extends HTMLElement>() {
  const focusedRef = useRef(false);
  const keyboardVisibleRef = useRef(false);

  const syncWithVisualViewport = useCallback(() => {
    if (!focusedRef.current) {
      return;
    }

    const keyboardInset = getVisualKeyboardInset();

    if (keyboardInset === null || keyboardInset > 80) {
      keyboardVisibleRef.current = true;
      emitMobileFooterVisibility(true);
      return;
    }

    if (keyboardVisibleRef.current) {
      keyboardVisibleRef.current = false;
      emitMobileFooterVisibility(false);
    }
  }, []);

  useEffect(() => {
    const viewport =
      typeof window !== "undefined" ? window.visualViewport : undefined;

    if (!viewport) {
      return () => {
        if (focusedRef.current) {
          keyboardVisibleRef.current = false;
          emitMobileFooterVisibility(false);
        }
      };
    }

    viewport.addEventListener("resize", syncWithVisualViewport);
    viewport.addEventListener("scroll", syncWithVisualViewport);

    return () => {
      viewport.removeEventListener("resize", syncWithVisualViewport);
      viewport.removeEventListener("scroll", syncWithVisualViewport);

      if (focusedRef.current) {
        keyboardVisibleRef.current = false;
        emitMobileFooterVisibility(false);
      }
    };
  }, [syncWithVisualViewport]);

  const onFocus = useCallback<FocusEventHandler<T>>(() => {
    focusedRef.current = true;
    keyboardVisibleRef.current = false;
    emitMobileFooterVisibility(true);
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(syncWithVisualViewport);
    }
  }, [syncWithVisualViewport]);

  const onBlur = useCallback<FocusEventHandler<T>>(() => {
    focusedRef.current = false;
    keyboardVisibleRef.current = false;
    emitMobileFooterVisibility(false);
  }, []);

  return { onFocus, onBlur };
}
